import clsx from 'clsx'
import { memo, useEffect, useRef, useState } from 'react'
import Linked from 'shared-components/service/Linked'
import AppImage from 'shared-components/ui/AppImage'
import ScoreRing from 'shared-components/ui/ScoreRing'
import styles from './DealHuntProductCard.module.css'
import { badgeToImage } from './badgeImage'
import { cardTitle, type ProductCardModel } from './cardModel'
import VersusYours from 'components/owner-notes/VersusYours'
import CardOwners from 'components/owner-notes/CardOwners'
import CardBottomLine from './CardBottomLine'
import PinToCompare from 'components/owner-notes/PinToCompare'
import { cardOwnersFromCard } from 'lib/owner-notes/card-owners'
import { useOnScreenOnce } from 'lib/owner-notes/analytics'
import { amazonUrl, dealHuntProductHref, formatCents } from 'lib/dealhunt/url'
import { publicConfig } from 'config'
import { useCatalogEvent, type CardListContext } from 'lib/analytics/catalog'
import { isCatalogEventName, type CatalogEventParams } from 'shared-code/app-events'

interface DealHuntProductCardProps {
  product: ProductCardModel
  className?: string
  /** When set, the primary CTA opens the retailer instead of the SoundGuys PDP. */
  ctaToRetailer?: boolean
  /** Which list + position the card sits in; enables `<page>_card_click` etc. */
  list?: CardListContext
}

/**
 * Product grid card in the SoundGuys presentation: product image, editorial badge,
 * matched-spec pills, SoundGuys review score + "Read our review" link, then price.
 * SoundGuys deliberately does NOT surface DealHunt's deal scores / deal pills / price
 * signals — that "deal" vocabulary is DealHunt's, not SoundGuys'. `ctaToRetailer` only
 * selects the primary CTA — "Buy on Amazon" (category/search grids) vs "View product" →
 * PDP (PDP alternatives) — it no longer switches an entire skin.
 */
const DealHuntProductCard: React.VFC<DealHuntProductCardProps> = ({
  product,
  className,
  ctaToRetailer = false,
  list,
}) => {
  const trailingSlash = publicConfig('useTrailingSlash')
  const pdpHref = product.fullSlug
    ? dealHuntProductHref(product.fullSlug, product.brand, trailingSlash, product.brandSlug)
    : null
  const retailerHref = amazonUrl(product.asin, product.domain, product.shortIoUrl)
  const ctaHref = ctaToRetailer ? retailerHref : pdpHref ?? retailerHref

  // Long matched-spec strings (e.g. a full surround-format list) are truncated to
  // one line to keep card height uniform; tapping expands to the full value.
  const [expandedSpec, setExpandedSpec] = useState<string | null>(null)
  const displayTitle = cardTitle(product)
  const { badge } = product
  const price = formatCents(product.currentPriceCents, product.domain)
  const usedPrice = formatCents(product.usedPriceCents, product.domain)

  const { sponsored } = product
  const { track, scope } = useCatalogEvent()
  // The sponsored slot's own pair (`<page_type>_sponsored_slot_impression` / `_click`): the
  // sponsored post and the line it promotes.
  const trackSponsored = (action: 'sponsored_slot_impression' | 'sponsored_slot_click'): void => {
    const name = `${scope?.page_type ?? ''}_${action}`
    if (!sponsored || !isCatalogEventName(name)) {
      return
    }
    track(name, {
      target_id: sponsored.postId,
      target_product_id: product.key,
      target_product_name: displayTitle,
      position: list?.position ?? null,
    })
  }
  // `<page_type>_card_click` / `_buy_click` / `_review_click` — the same three actions on
  // every listing surface; `list_name` + `position` say which list. The sponsored card's
  // carry `kind: sponsored_slot` (contract C1 "promotion") and also report the slot click.
  const trackCard = (
    action: 'card_click' | 'buy_click' | 'review_click',
    extra: CatalogEventParams = {}
  ): void => {
    // Cards render inside a catalog provider only; coverage hubs register no card events.
    const name = `${scope?.page_type ?? ''}_${action}`
    if (!isCatalogEventName(name)) {
      return
    }
    track(name, {
      ...list,
      target_product_name: displayTitle,
      target_product_slug: product.fullSlug,
      target_asin: product.asin,
      target_brand: product.brand,
      target_score: product.reviewScore,
      target_badge: badge?.key ?? null,
      price_cents: product.currentPriceCents,
      ...(sponsored ? { kind: 'sponsored_slot' } : {}),
      ...extra,
    })
    trackSponsored('sponsored_slot_click')
  }
  // Impression: once the sponsored card is ≥30 % on screen, once per card instance (a refetch
  // that keeps the same slot keeps the instance). Every other card never observes.
  const articleRef = useRef<HTMLElement>(null)
  const sponsoredSeen = useOnScreenOnce(articleRef, sponsored !== null)
  useEffect(() => {
    if (sponsoredSeen) {
      trackSponsored('sponsored_slot_impression')
    }
    // Fires once when `seen` flips; the tracker is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsoredSeen])
  const onCardClick = (): void =>
    trackCard(pdpHref ? 'card_click' : 'buy_click', { location: 'card' })
  const onReviewClick = (): void => trackCard('review_click', { location: 'card' })
  // One slot under the score row: the Owners block when the product has owner reviews, else
  // the review's bottom line (contract C0) — never both, never an empty box.
  const ownersView = cardOwnersFromCard(product.owners)

  return (
    <article
      ref={articleRef}
      className={clsx(styles.card, { [styles.cardSponsored]: sponsored }, className)}
    >
      <Linked
        href={pdpHref ?? ctaHref}
        className={styles.imageLink}
        aria-label={displayTitle}
        onClick={onCardClick}
      >
        <div className={styles.imageWrap}>
          {product.imageUrl ? (
            <>
              <div className={styles.imageSkeleton} aria-hidden="true" />
              {/* Remote DealHunt CDN image — plain img avoids next/image domain config. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                className={styles.image}
                src={product.imageUrl}
                alt={displayTitle}
                width={300}
                height={300}
                loading="lazy"
                decoding="async"
              />
            </>
          ) : (
            <div className={styles.imagePlaceholder} aria-hidden="true" />
          )}
          {badge?.imageUrl && (
            // SoundGuys badge shield (Editor's Choice / Recommended), rendered through
            // the shared AppImage <picture> path. Remote SG image.
            <AppImage
              layout="raw"
              sizes={46}
              className={styles.badgePicture}
              imageClassName={styles.badgeImg}
              image={badgeToImage(badge)}
            />
          )}
          {badge && !badge.imageUrl && (
            <span className={clsx(styles.sgBadge, styles[`badge_${badge.key}`])}>
              {badge.label}
            </span>
          )}
          {product.usedCondition && (
            <span className={clsx(styles.pill, styles.usedPill)}>
              {product.usedCondition === 'REFURBISHED' ? 'Refurb' : 'Used'}
            </span>
          )}
        </div>
      </Linked>

      <div className={styles.body}>
        {sponsored && <div className={styles.sponsoredEyebrow}>{'Sponsored'}</div>}
        {/* Top row: the category eyebrow, and on the right "Pin to compare" (lab compare
            drawer, 2026-09-09) — null outside the category page or without a declaration. */}
        <div className={styles.topRow}>
          {product.categoryLabel && <div className={styles.category}>{product.categoryLabel}</div>}
          <PinToCompare
            nodeId={product.key}
            slug={product.fullSlug}
            name={displayTitle}
            pdpHref={pdpHref}
            clearbuyProductId={product.clearbuyProductId ?? null}
          />
        </div>
        <Linked href={pdpHref ?? ctaHref} className={styles.titleLink} onClick={onCardClick}>
          <h3 className={styles.title}>{displayTitle}</h3>
        </Linked>

        {product.matchedRatings.length > 0 && (
          // Matched score filters (MDAQS Sound, ANC…) as mini ring gauges — the
          // PDP's visual language — so the scale ("4.9 /5") is explicit next to
          // the /10 SoundGuys score chip below.
          <div className={styles.ratingRings}>
            {product.matchedRatings.map(rating => (
              <div key={rating.key} className={styles.ratingGauge}>
                <ScoreRing
                  className={styles.ratingRing}
                  score={rating.value}
                  maxScore={rating.scaleMax}
                >
                  <span className={styles.ratingRingText}>
                    <span className={styles.ratingRingVal}>{rating.value.toFixed(1)}</span>
                    <span className={styles.ratingRingMax}>{`/ ${rating.scaleMax}`}</span>
                  </span>
                </ScoreRing>
                <span className={styles.ratingLabel}>{rating.label}</span>
              </div>
            ))}
          </div>
        )}

        {(product.matchedSpecs.length > 0 || product.releaseDateLabel !== null) && (
          <div className={styles.specPills}>
            {product.releaseDateLabel !== null && (
              // Inert (no ✓, not expandable): it echoes the active sort, not a
              // matched filter. Absent entirely on undated lines.
              <span className={styles.datePill}>{product.releaseDateLabel}</span>
            )}
            {product.matchedSpecs.map(spec => (
              <button
                key={spec}
                type="button"
                className={clsx(styles.specPill, {
                  [styles.specPillExpanded]: expandedSpec === spec,
                })}
                title={spec}
                aria-expanded={expandedSpec === spec}
                onClick={() => setExpandedSpec(expandedSpec === spec ? null : spec)}
              >
                {`✓ ${spec}`}
              </button>
            ))}
          </div>
        )}

        {product.translation && (
          // Translation at a glance (AI Translation Earbuds): device class, languages,
          // offline, subscription — static facts, not filter echoes, so inert spans.
          // Null on every hit without a translation.* spec.
          <div className={styles.translation}>
            {product.translation.chips.length > 0 && (
              <div className={styles.specPills}>
                {product.translation.chips.map(chip => (
                  <span
                    key={chip.text}
                    className={clsx(styles.translationChip, {
                      [styles.translationChipDark]: chip.tone === 'translatorFirst',
                      [styles.translationChipLight]: chip.tone === 'audioFirst',
                    })}
                  >
                    {chip.text}
                  </span>
                ))}
              </div>
            )}
            {product.translation.modes && (
              <div className={styles.translationModes}>{product.translation.modes}</div>
            )}
          </div>
        )}

        {usedPrice && (
          <div className={styles.usedOffer}>
            {'Used from '}
            {usedPrice}
          </div>
        )}

        {(product.reviewScore !== null || product.reviewUrl) && (
          <div className={styles.reviewRow}>
            {product.reviewScore !== null && (
              <span className={styles.scoreChip}>
                {product.reviewScore.toFixed(1)}
                <span className={styles.scoreChipMax}>{'/10'}</span>
              </span>
            )}
            <span className={styles.reviewMeta}>
              {product.reviewScore !== null && (
                <span className={styles.scoreLabel}>{'SoundGuys score'}</span>
              )}
              {product.reviewUrl && (
                <Linked
                  href={product.reviewUrl}
                  className={styles.reviewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onReviewClick}
                >
                  {'Read our review ›'}
                </Linked>
              )}
            </span>
          </div>
        )}

        {/* Owner notes: the Owners block (server-rendered counts, #115) on any card whose
            product has a note, then — after hydration — the "Versus yours" block against the
            reader's declared product, or the own tile's ask. Category cards only — search
            hits and PDP variants carry no owner data, so both render null. A card with no
            Owners block shows the review's bottom line in that slot instead (contract C0). */}
        <CardOwners
          owners={ownersView}
          claimedHours={product.batteryHours}
          pdpHref={pdpHref}
          nodeId={product.key}
          productName={displayTitle}
        />
        {ownersView === null && product.reviewOverview !== null && (
          <CardBottomLine
            overview={product.reviewOverview}
            reviewUrl={product.reviewUrl}
            onReviewClick={onReviewClick}
          />
        )}
        <VersusYours
          nodeId={product.key}
          slug={product.fullSlug}
          productName={displayTitle}
          pdpHref={pdpHref}
          numbers={{
            axes: product.axes,
            batteryHours: product.batteryHours,
            priceCents: product.currentPriceCents,
            sgScore: product.reviewScore,
            clearbuyProductId: product.clearbuyProductId,
            compareSpecs: product.compareSpecs,
          }}
        />

        <div className={styles.commerce}>
          <div className={styles.priceRow}>
            {price && <span className={styles.price}>{price}</span>}
            {price && ctaToRetailer && <span className={styles.atAmazon}>{'at Amazon'}</span>}
          </div>

          <Linked
            href={ctaHref}
            className={styles.cta}
            target={ctaToRetailer ? '_blank' : undefined}
            rel={ctaToRetailer ? 'nofollow sponsored noopener' : undefined}
            onClick={() => {
              if (ctaToRetailer) {
                trackCard('buy_click', { location: 'card' })
              } else {
                onCardClick()
              }
            }}
          >
            {ctaToRetailer ? 'Buy on Amazon' : 'View product'}
            {ctaToRetailer && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 12 12"
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3.5 8.5 8.5 3.5" />
                <path d="M4.5 3.5h4v4" />
              </svg>
            )}
          </Linked>
        </div>
        {sponsored?.sponsorName && (
          <div className={styles.sponsoredBy}>{`Brought to you by ${sponsored.sponsorName}`}</div>
        )}
      </div>
    </article>
  )
}

// Memoized: a category grid holds 24+ cards and re-renders on template state that changes
// no card (filters drawer, the declared pair's pins). Callers keep `product` and `list`
// referentially stable (CategoryBrowseTemplate memoizes both).
export default memo(DealHuntProductCard)
